import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Send, ArrowLeft, Plus, Mail, MessageCircle, Trash2, Eye, Check,
  ChevronRight, ChevronLeft, X, Users, Calendar,
} from 'lucide-react';
import api from '../utils/api';

const STATUS_LABELS = {
  draft: { label: 'Entwurf', color: 'bg-gray-100 text-gray-700' },
  ready: { label: 'Bereit', color: 'bg-blue-100 text-blue-700' },
  scheduled: { label: 'Geplant', color: 'bg-purple-100 text-purple-700' },
  sending: { label: 'Wird versendet', color: 'bg-yellow-100 text-yellow-700' },
  sent: { label: 'Versendet', color: 'bg-green-100 text-green-700' },
  partially_sent: { label: 'Teilweise versendet', color: 'bg-orange-100 text-orange-700' },
  failed: { label: 'Fehlgeschlagen', color: 'bg-red-100 text-red-700' },
};

const AUDIENCE_LABELS = {
  all_active: 'Alle aktiven Kunden',
  with_orders: 'Kunden mit früheren Bestellungen',
  no_orders: 'Kunden ohne frühere Bestellung',
  custom: 'Manuelle Auswahl',
};

const CHANNEL_LABELS = { email: 'E-Mail', whatsapp: 'WhatsApp' };
const PROVIDER_LABELS = { mock: 'Testversand (mocked)', resend: 'Resend', twilio: 'Twilio', unknown: 'Unbekannt' };
const DELIVERY_STATUS_LABELS = { sent: 'Versendet', failed: 'Fehlgeschlagen', delivered: 'Zugestellt', opened: 'Geöffnet', clicked: 'Geklickt' };

// ============= CAMPAIGN LIST =============
const CampaignsList = ({ campaigns, onOpen, onNew, onDelete, onReload }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-lg font-semibold">Meine Kampagnen</h2>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onReload} data-testid="reload-campaigns-btn">
          Neu laden
        </Button>
        <Button onClick={onNew} data-testid="new-campaign-btn">
          <Plus className="h-4 w-4 me-1" /> Neue Kampagne
        </Button>
      </div>
    </div>
    {campaigns.length === 0 ? (
      <div className="text-center py-16 bg-white border border-dashed border-gray-300 rounded-lg">
        <Send className="h-10 w-10 mx-auto text-gray-300 mb-2" />
        <p className="text-gray-500 mb-4">Noch keine Kampagnen angelegt.</p>
        <Button onClick={onNew}><Plus className="h-4 w-4 me-1" /> Erste Kampagne erstellen</Button>
      </div>
    ) : (
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b text-xs text-gray-600 uppercase">
            <tr>
              <th className="text-start px-4 py-2">Name</th>
              <th className="text-start px-4 py-2">Status</th>
              <th className="text-start px-4 py-2">Kanäle</th>
              <th className="text-end px-4 py-2">Empfänger</th>
              <th className="text-end px-4 py-2">Versendet</th>
              <th className="text-start px-4 py-2">Erstellt</th>
              <th className="text-center px-4 py-2">Aktion</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {campaigns.map(c => {
              const s = STATUS_LABELS[c.status] || STATUS_LABELS.draft;
              return (
                <tr key={c.id} data-testid={`campaign-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium">{c.campaign_name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${s.color}`}>{s.label}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {c.email_enabled && <Mail className="h-4 w-4 inline text-blue-600 me-1" title="E-Mail" />}
                    {c.whatsapp_enabled && <MessageCircle className="h-4 w-4 inline text-green-600" title="WhatsApp" />}
                  </td>
                  <td className="px-4 py-3 text-end">{c.recipient_count ?? 0}</td>
                  <td className="px-4 py-3 text-end">{c.sent_count ?? 0}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('de-DE') : '-'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Button size="sm" variant="outline" onClick={() => onOpen(c)} data-testid={`open-campaign-${c.id}`}>
                      Öffnen
                    </Button>
                    {['draft', 'ready'].includes(c.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="ms-2 text-red-600"
                        onClick={() => onDelete(c)}
                        data-testid={`delete-campaign-${c.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ============= CAMPAIGN WIZARD (multi-step) =============
const CampaignWizard = ({ campaign, onSave, onCancel, onSent }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    campaign_name: campaign?.campaign_name || '',
    offer_title: campaign?.offer_title || '',
    offer_message: campaign?.offer_message || '',
    email_subject: campaign?.email_subject || '',
    audience_type: campaign?.audience_type || 'all_active',
    email_enabled: campaign?.email_enabled ?? true,
    whatsapp_enabled: campaign?.whatsapp_enabled ?? false,
  });

  const isSent = ['sent', 'sending', 'partially_sent'].includes(campaign?.status);

  const persist = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/reseller/campaigns/${campaign.id}`, form);
      onSave(res.data.campaign);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    try {
      const res = await api.get(`/reseller/campaigns/${campaign.id}/recipients-preview`);
      setPreview(res.data);
    } catch (e) {
      toast.error('Empfänger-Vorschau fehlgeschlagen');
    }
  };

  const testSend = async () => {
    try {
      await persist();
      const res = await api.post(`/reseller/campaigns/${campaign.id}/test-send`);
      toast.success('Testversand an dich ausgelöst');
      console.log('Test send results', res.data.results);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Testversand fehlgeschlagen');
    }
  };

  const finalSend = async () => {
    setSending(true);
    try {
      await persist();
      const res = await api.post(`/reseller/campaigns/${campaign.id}/send?confirm=true`);
      toast.success(`Kampagne versendet an ${res.data.recipients} Empfänger`);
      setConfirming(false);
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  const next = async () => {
    if (isSent) return;
    if (step === 1) {
      if (!form.campaign_name?.trim()) {
        toast.error('Bitte einen Kampagnennamen angeben');
        return;
      }
    }
    if (step === 3) {
      if (!form.email_enabled && !form.whatsapp_enabled) {
        toast.error('Mindestens einen Versandkanal aktivieren');
        return;
      }
    }
    if (step === 4) {
      await persist();
    }
    if (step === 5) {
      await loadPreview();
    }
    setStep(step + 1);
  };

  const prev = () => step > 1 && setStep(step - 1);

  return (
    <div className="bg-white border rounded-lg p-6" data-testid="campaign-wizard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold" data-testid="wizard-title">
            {campaign?.campaign_name || 'Neue Kampagne'}
          </h2>
          <p className="text-sm text-gray-500">Schritt {step} von 6</p>
        </div>
        <Button variant="ghost" onClick={onCancel} data-testid="wizard-close-btn">
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Progress dots */}
      <div className="flex gap-2 mb-6">
        {[1, 2, 3, 4, 5, 6].map(n => (
          <div
            key={n}
            className={`flex-1 h-1 rounded ${n <= step ? 'bg-primary' : 'bg-gray-200'}`}
          />
        ))}
      </div>

      {isSent && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
          Diese Kampagne wurde bereits versendet ({campaign.sent_count}/{campaign.recipient_count} zugestellt).
          Änderungen sind nicht mehr möglich.
        </div>
      )}

      {/* Step 1 – Basics */}
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">1. Kampagnendetails</h3>
          <div>
            <label className="block text-sm font-medium mb-1">Kampagnenname *</label>
            <Input
              value={form.campaign_name}
              onChange={(e) => setForm({ ...form, campaign_name: e.target.value })}
              disabled={isSent}
              data-testid="input-campaign-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Angebotstitel</label>
            <Input
              value={form.offer_title}
              onChange={(e) => setForm({ ...form, offer_title: e.target.value })}
              disabled={isSent}
              placeholder="z.B. Sommeraktion – 20% Rabatt"
              data-testid="input-offer-title"
            />
          </div>
        </div>
      )}

      {/* Step 2 – Audience */}
      {step === 2 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Users className="h-5 w-5" /> 2. Empfänger auswählen
          </h3>
          <p className="text-sm text-gray-500">
            Die Kampagne geht ausschließlich an Endkunden Ihres Shops mit passender Einwilligung.
          </p>
          <div className="space-y-2">
            {[
              { v: 'all_active', l: 'Alle aktiven Kunden' },
              { v: 'with_orders', l: 'Kunden mit früheren Bestellungen' },
              { v: 'no_orders', l: 'Kunden ohne frühere Bestellung' },
            ].map(opt => (
              <label
                key={opt.v}
                className={`flex items-center gap-2 p-3 border rounded cursor-pointer ${
                  form.audience_type === opt.v ? 'border-primary bg-primary/5' : 'border-gray-200'
                }`}
                data-testid={`audience-${opt.v}`}
              >
                <input
                  type="radio"
                  name="audience"
                  value={opt.v}
                  checked={form.audience_type === opt.v}
                  onChange={() => setForm({ ...form, audience_type: opt.v })}
                  disabled={isSent}
                />
                {opt.l}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Step 3 – Channels */}
      {step === 3 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">3. Versandkanäle</h3>
          <p className="text-sm text-gray-500">
            Es werden nur Kunden angeschrieben, die für den jeweiligen Kanal ihre Einwilligung erteilt haben.
          </p>
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 border rounded cursor-pointer" data-testid="channel-email">
              <input
                type="checkbox"
                checked={form.email_enabled}
                onChange={(e) => setForm({ ...form, email_enabled: e.target.checked })}
                disabled={isSent}
              />
              <Mail className="h-5 w-5 text-blue-600" />
              <span>E-Mail</span>
            </label>
            <label className="flex items-center gap-3 p-3 border rounded cursor-pointer" data-testid="channel-whatsapp">
              <input
                type="checkbox"
                checked={form.whatsapp_enabled}
                onChange={(e) => setForm({ ...form, whatsapp_enabled: e.target.checked })}
                disabled={isSent}
              />
              <MessageCircle className="h-5 w-5 text-green-600" />
              <span>WhatsApp</span>
            </label>
          </div>
        </div>
      )}

      {/* Step 4 – Message */}
      {step === 4 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">4. Nachricht</h3>
          {form.email_enabled && (
            <div>
              <label className="block text-sm font-medium mb-1">E-Mail-Betreff</label>
              <Input
                value={form.email_subject}
                onChange={(e) => setForm({ ...form, email_subject: e.target.value })}
                placeholder="Neues Angebot bei uns"
                disabled={isSent}
                data-testid="input-email-subject"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Nachrichtentext</label>
            <textarea
              value={form.offer_message}
              onChange={(e) => setForm({ ...form, offer_message: e.target.value })}
              rows={5}
              className="w-full border rounded p-2"
              placeholder="Verwenden Sie Platzhalter: {{shop_name}}, {{product_name}}, {{offer_price}}, {{offer_link}}"
              disabled={isSent}
              data-testid="input-message"
            />
            <p className="text-xs text-gray-500 mt-1">
              Verfügbare Platzhalter: <code>{'{{shop_name}}'}</code>, <code>{'{{product_name}}'}</code>,{' '}
              <code>{'{{offer_price}}'}</code>, <code>{'{{offer_link}}'}</code>
            </p>
          </div>
        </div>
      )}

      {/* Step 5 – Test send + preview */}
      {step === 5 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Eye className="h-5 w-5" /> 5. Vorschau & Testversand
          </h3>
          <div className="bg-gray-50 border rounded p-4">
            <h4 className="text-sm font-semibold mb-2">Vorschau</h4>
            <p className="font-medium">{form.offer_title || form.campaign_name}</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap mt-2">{form.offer_message || <em>(kein Text)</em>}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={testSend} disabled={isSent} data-testid="test-send-btn">
              <Send className="h-4 w-4 me-1" /> Testversand an mich
            </Button>
          </div>
        </div>
      )}

      {/* Step 6 – Confirm */}
      {step === 6 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Check className="h-5 w-5" /> 6. Zusammenfassung & Versand
          </h3>
          <div className="bg-gray-50 border rounded p-4 space-y-2 text-sm">
            <div><strong>Kampagne:</strong> {form.campaign_name}</div>
            <div><strong>Angebotstitel:</strong> {form.offer_title || <em>—</em>}</div>
            <div><strong>Empfängergruppe:</strong> {AUDIENCE_LABELS[form.audience_type] || form.audience_type}</div>
            <div><strong>Kanäle:</strong> {form.email_enabled && CHANNEL_LABELS.email} {form.whatsapp_enabled && ` ${CHANNEL_LABELS.whatsapp}`}</div>
            {preview && (
              <div className="pt-2 border-t mt-2 space-y-1">
                <div><strong>Empfänger gesamt:</strong> {preview.total}</div>
                <div className="text-blue-700">✉ E-Mail berechtigt: {preview.email_eligible}</div>
                <div className="text-green-700">💬 WhatsApp berechtigt: {preview.whatsapp_eligible}</div>
                <div className="text-gray-500">Ohne passenden Kanal: {preview.no_channel}</div>
              </div>
            )}
          </div>
          {!isSent && (
            <Button
              onClick={() => setConfirming(true)}
              disabled={sending}
              data-testid="send-campaign-btn"
              className="w-full"
            >
              <Send className="h-4 w-4 me-1" /> Versand vorbereiten
            </Button>
          )}

          {/* Two-step confirmation modal */}
          {confirming && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="confirm-modal">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h3 className="font-bold mb-2">Versand bestätigen</h3>
                <p className="text-sm text-gray-700 mb-4">
                  Möchten Sie dieses Angebot wirklich an die ausgewählten Empfänger versenden?
                  Dieser Vorgang kann nicht rückgängig gemacht werden.
                </p>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setConfirming(false)} data-testid="confirm-cancel-btn">
                    Abbrechen
                  </Button>
                  <Button onClick={finalSend} disabled={sending} data-testid="confirm-send-btn">
                    {sending ? 'Wird versendet …' : 'Jetzt versenden'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Nav */}
      <div className="flex justify-between mt-6 pt-4 border-t">
        <Button variant="outline" onClick={prev} disabled={step === 1} data-testid="wizard-prev-btn">
          <ChevronLeft className="h-4 w-4 me-1" /> Zurück
        </Button>
        <div className="flex gap-2">
          {step < 6 && (
            <>
              <Button variant="outline" onClick={persist} disabled={saving || isSent} data-testid="wizard-save-btn">
                {saving ? 'Speichere…' : 'Zwischenspeichern'}
              </Button>
              <Button onClick={next} disabled={isSent} data-testid="wizard-next-btn">
                Weiter <ChevronRight className="h-4 w-4 ms-1" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ============= DELIVERIES LOG =============
const DeliveriesLog = ({ campaign, onBack }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/reseller/campaigns/${campaign.id}/deliveries`);
        setDeliveries(res.data || []);
      } catch (e) {
        toast.error('Fehler beim Laden');
      } finally {
        setLoading(false);
      }
    })();
  }, [campaign.id]);

  return (
    <div className="bg-white border rounded-lg p-4" data-testid="deliveries-log">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">Versandprotokoll – {campaign.campaign_name}</h3>
        <Button variant="outline" size="sm" onClick={onBack}>Schließen</Button>
      </div>
      {loading ? (
        <p className="text-gray-500">Laden…</p>
      ) : deliveries.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500">Noch keine Zustellungen protokolliert.</p>
          <p className="text-xs text-gray-400 mt-2">
            Empfänger ohne Marketing-Einwilligung für den gewählten Kanal werden übersprungen.
            Bitten Sie Ihre Kunden in ihren Benachrichtigungseinstellungen um Zustimmung.
          </p>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="border-b text-gray-600">
            <tr>
              <th className="text-start py-2">Kanal</th>
              <th className="text-start py-2">Provider</th>
              <th className="text-start py-2">Status</th>
              <th className="text-start py-2">Versendet</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {deliveries.map(d => (
              <tr key={d.id}>
                <td className="py-2">
                  {d.channel === 'email' ? <Mail className="h-4 w-4 inline text-blue-600" /> : <MessageCircle className="h-4 w-4 inline text-green-600" />}
                  <span className="ms-1">{CHANNEL_LABELS[d.channel] || d.channel}</span>
                </td>
                <td className="py-2">{PROVIDER_LABELS[d.provider] || d.provider}</td>
                <td className="py-2">
                  <span className={`px-2 py-0.5 rounded text-xs ${d.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {DELIVERY_STATUS_LABELS[d.status] || d.status}
                  </span>
                </td>
                <td className="py-2 text-gray-500">
                  {d.sent_at ? new Date(d.sent_at).toLocaleString('de-DE') : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ============= MAIN PAGE =============
export default function MyShopMarketingPage() {
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // 'list' | 'wizard' | 'deliveries'
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reseller/campaigns');
      setCampaigns(res.data || []);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createNew = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      const res = await api.post('/reseller/campaigns', {
        campaign_name: newName,
        audience_type: 'all_active',
        email_enabled: true,
        whatsapp_enabled: false,
      });
      setSelected(res.data.campaign);
      setView('wizard');
      setShowNewForm(false);
      setNewName('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Kampagne "${c.campaign_name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/reseller/campaigns/${c.id}`);
      toast.success('Kampagne gelöscht');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="marketing-page">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/my-shop')} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 me-1" /> Zurück
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Send className="h-6 w-6" /> Marketing &amp; Angebote
          </h1>
          <p className="text-gray-600">
            Versenden Sie B2C-Kampagnen an Ihre eigenen Endkunden per E-Mail und WhatsApp.
          </p>
        </div>
      </div>

      {view === 'list' && (
        <>
          {showNewForm && (
            <form
              onSubmit={createNew}
              className="bg-gray-50 border rounded-lg p-4 mb-6 flex items-end gap-2"
              data-testid="new-campaign-form"
            >
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Neue Kampagne – Name</label>
                <Input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="z.B. Sommer-Angebot Bartöl"
                  data-testid="new-campaign-name-input"
                />
              </div>
              <Button type="submit" data-testid="new-campaign-create-btn">Anlegen</Button>
              <Button type="button" variant="outline" onClick={() => setShowNewForm(false)}>Abbrechen</Button>
            </form>
          )}
          <CampaignsList
            campaigns={campaigns}
            onOpen={(c) => { setSelected(c); setView('wizard'); }}
            onNew={() => setShowNewForm(true)}
            onDelete={handleDelete}
            onReload={load}
          />
          {campaigns.some(c => ['sent', 'partially_sent', 'sending'].includes(c.status)) && (
            <div className="mt-4">
              <Button
                variant="outline"
                onClick={() => {
                  const sent = campaigns.find(c => ['sent', 'partially_sent'].includes(c.status));
                  if (sent) { setSelected(sent); setView('deliveries'); }
                }}
                data-testid="view-deliveries-btn"
              >
                <Calendar className="h-4 w-4 me-1" /> Versandprotokoll der letzten Kampagne
              </Button>
            </div>
          )}
        </>
      )}

      {view === 'wizard' && selected && (
        <CampaignWizard
          campaign={selected}
          onSave={(c) => setSelected(c)}
          onCancel={() => { setView('list'); load(); }}
          onSent={() => { setView('deliveries'); load(); }}
        />
      )}

      {view === 'deliveries' && selected && (
        <DeliveriesLog campaign={selected} onBack={() => setView('list')} />
      )}
    </div>
  );
}
